import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Stack, createStyles} from '@mantine/core';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {AgentTypeError} from 'llm/chat-library/ChatDraftState';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import NoModelsInstalledEmptyState from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {type ScientificModelNode, supportsMSA} from 'model-hub/scientific-models/ScientificModelMetadata';
import notifications from 'notifications';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as net from 'util/Net';
import sortedBy from 'util/sortedBy';

import InputsSection from './InputsSection';
import ModelSection from './ModelSection';
import {
  composeModelCapabilities,
  getSubmitDisabledReason,
  type PredictionInput,
  type PredictionInputsUpdate,
} from './newPredictionState';
import {resolveBatchName, runMultiModelSubmit, stripModelPrefix} from './newPredictionSubmit';
import NewPredictionTopBar from './NewPredictionTopBar';

interface NewPredictionPageProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  onBack: () => void;
  onSubmitted: () => void;
  preselectedModel?: ScientificModelNode;
}

const DEFAULT_AUTO_NAME = 'New prediction batch';

const CONTENT_MAX_WIDTH_PX = 1160;

export default function NewPredictionPage({
  availableModels,
  onBack,
  onSubmitted,
  preselectedModel,
}: NewPredictionPageProps): JSX.Element {
  const {classes} = useStyles();
  const [selectedModels, setSelectedModels] = useState<Array<ScientificModelNode>>([]);
  // Per-model "Use MSAs" state keyed by externalReference. MSA resolves per-model at submit and stays
  // out of the composed capability set.
  const [msaEnabledByModel, setMSAEnabledByModel] = useState<Record<string, boolean>>({});
  const [batchName, setBatchName] = useState('');
  // The model-derived default, in both forms: `full` keeps the model name the backend embeds (used
  // when a single model is selected), `base` has it stripped (used when several models are selected,
  // since submit prefixes each batch with its own model). Null until the request resolves.
  const [generatedName, setGeneratedName] = useState<{full: string; base: string} | null>(null);
  // A name the user typed is theirs to keep; an untouched field keeps tracking the selected model.
  // Clearing the field hands the name back to the model-generated default.
  const isBatchNameUserEditedRef = useRef(false);
  // Start empty so the upload drop zone is the first thing shown; the user adds inputs by uploading
  // a CSV or choosing "Enter data".
  const [inputs, setInputs] = useState<Array<PredictionInput>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<AgentTypeError | null>(null);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_OPENED);
  }, []);

  useEffect(() => {
    if (availableModels.length === 0) {
      return;
    }
    // Only preselect a model when one was explicitly requested (e.g. a per-model "New prediction"
    // card). The generic entry starts empty so the user adds a model via the "+".
    if (
      preselectedModel == null ||
      !availableModels.some(
        (availableModel) => availableModel.externalReference === preselectedModel.externalReference
      )
    ) {
      return;
    }
    setSelectedModels((currentModels) => (currentModels.length > 0 ? currentModels : [preselectedModel]));
    setMSAEnabledByModel((current) => ({
      [preselectedModel.externalReference]: supportsMSA(preselectedModel),
      ...current,
    }));
  }, [availableModels, preselectedModel]);

  const firstModel = selectedModels.length > 0 ? selectedModels[0] : null;
  const firstModelReference = firstModel?.externalReference;

  // Keyed on the *first* model only: adding a second model doesn't invalidate the generated name, so
  // re-fetching would only blank the field and hand back the first model's name anyway.
  useEffect(() => {
    if (firstModel == null) {
      setGeneratedName(null);
      return;
    }

    let cancelled = false;
    net
      .postJSONPromise({
        url: '/1/api/model-hub/prediction-batch:generate-name',
        data: {model: firstModel.externalReference},
      })
      .then((resp: {defaultName: string}) => {
        if (!cancelled) {
          setGeneratedName({full: resp.defaultName, base: stripModelPrefix(resp.defaultName, firstModel)});
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Only the first model's identity determines the generated name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstModelReference]);

  // With several models each batch is prefixed with its own model at submit, so the shared base must
  // not hard-code one of them. A single model gets no prefix, so it keeps the full generated name.
  const autoName =
    generatedName == null
      ? DEFAULT_AUTO_NAME
      : selectedModels.length > 1
        ? generatedName.base
        : generatedName.full;

  // Track the resolved default until the user takes the name over. This also re-syncs when the model
  // count crosses 1↔2 and the default's shape changes, without ever clearing what the user typed.
  useEffect(() => {
    if (!isBatchNameUserEditedRef.current) {
      setBatchName(generatedName == null ? '' : autoName);
    }
  }, [autoName, generatedName]);

  const capabilities = useMemo(() => composeModelCapabilities(selectedModels), [selectedModels]);

  const disabledReason = useMemo(
    () =>
      getSubmitDisabledReason({
        inputs,
        hasModels: availableModels.length > 0,
        hasSelectedModel: selectedModels.length > 0,
        isSubmitting,
        maxAminoAcidCount: capabilities.maxAminoAcidCount,
        maxComponentsPerInput: capabilities.maxComponents,
        maxInputs: capabilities.maxBulkPredictionItems,
        minAminoAcidCount: capabilities.minAminoAcidCount,
        // Name the constraint owner generically; the composed set can span several models.
        modelName: selectedModels.length === 1 ? selectedModels[0].displayName : 'The selected models',
        moleculesSupported: capabilities.supportsMolecules,
        // AND-ed across the selection, so adding a monomer model correctly singularizes the copy.
        multimersSupported: capabilities.supportsMultimers,
      }),
    [availableModels, capabilities, inputs, isSubmitting, selectedModels]
  );

  // N inputs × M models → M batches, N jobs each. Surface the fan-out before submitting.
  const predictionCount = inputs.length * selectedModels.length;

  // Exactly what submit will name each batch, via the same helper submit uses, so the preview can't
  // drift from reality. Only meaningful with several models; one model shows its name in the field.
  const resolvedBatchNames = useMemo(
    () =>
      selectedModels.length > 1
        ? selectedModels.map((model) =>
            resolveBatchName({
              batchName: batchName.trim() || autoName,
              model,
              modelCount: selectedModels.length,
            })
          )
        : [],
    [autoName, batchName, selectedModels]
  );

  // Any edit invalidates a prior submit failure, so dismiss stale banners on the next change.
  const clearSubmitErrors = (): void => {
    setError(null);
    setCreditError(null);
  };

  const handleAddModel = (model: ScientificModelNode): void => {
    clearSubmitErrors();
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_MODEL_ADDED, {
      model: model.externalReference,
    });
    setSelectedModels((currentModels) =>
      currentModels.some((current) => current.externalReference === model.externalReference)
        ? currentModels
        : [...currentModels, model]
    );
    setMSAEnabledByModel((current) => ({[model.externalReference]: supportsMSA(model), ...current}));
  };

  const handleRemoveModel = (model: ScientificModelNode): void => {
    clearSubmitErrors();
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_MODEL_REMOVED, {
      model: model.externalReference,
    });
    setSelectedModels((currentModels) =>
      currentModels.filter((current) => current.externalReference !== model.externalReference)
    );
  };

  const handleMSAEnabledChange = (externalReference: string, enabled: boolean): void => {
    setMSAEnabledByModel((current) => ({...current, [externalReference]: enabled}));
  };

  const handleBatchNameChange = (name: string): void => {
    isBatchNameUserEditedRef.current = name.trim() !== '';
    setBatchName(name);
  };

  const handleInputsChange = (nextInputs: PredictionInputsUpdate): void => {
    clearSubmitErrors();
    setInputs(nextInputs);
  };

  const handleSubmit = async (): Promise<void> => {
    if (disabledReason != null || selectedModels.length === 0) {
      return;
    }

    // Model identity travels as a sorted, comma-joined string rather than an array: each metadata key
    // becomes a warehouse column, so a scalar stays directly groupable as a "which models ran
    // together" key without depending on how arrays get materialized.
    const submitMetadata = {
      models: sortedBy(selectedModels, ['externalReference'])
        .map((model) => model.externalReference)
        .join(','),
      modelCount: selectedModels.length,
      inputCount: inputs.length,
      predictionCount,
    };

    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_CLICKED, submitMetadata);
    setIsSubmitting(true);
    setError(null);
    setCreditError(null);
    try {
      const {succeeded, failures} = await runMultiModelSubmit({
        inputs,
        batchName: batchName.trim() || autoName,
        models: selectedModels,
        msaEnabledByModel,
      });
      // One outcome event per submit keeps click→outcome 1:1; the counts carry the fan-out detail, so a
      // partial failure reports as FAILED with a non-zero succeededCount.
      const outcomeMetadata = {
        ...submitMetadata,
        succeededCount: succeeded,
        failedCount: failures.length,
      };

      if (failures.length === 0) {
        const batchCount = succeeded;
        trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_SUCCEEDED, outcomeMetadata);
        notifications.success(
          `Created ${batchCount} prediction ${pluralize(batchCount, 'batch', 'batches')}.`
        );
        onSubmitted();
        return;
      }

      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_FAILED, outcomeMetadata);

      // Surface the first failure with the page's rich classification (credit banner, structured
      // message). Every non-failing model still submitted.
      const firstError = failures[0].error;
      const creditsError = getInsufficientCreditsAgentTypeError(firstError);
      if (creditsError) {
        setCreditError(creditsError);
        return;
      }
      const detail = getKnownSubmitErrorMessage(firstError) ?? getErrorMessage(firstError);
      if (selectedModels.length === 1) {
        // Single-model submit: surface the raw failure message, as before.
        if (detail != null) {
          setError(detail);
        }
        return;
      }
      // Multi-model submit: some batches may have succeeded, so report the fan-out outcome.
      const failedNames = failures.map((failure) => failure.modelName).join(', ');
      setError(
        `${succeeded} of ${selectedModels.length} batches created. Failed: ${failedNames}${
          detail != null ? ` (${detail})` : ''
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = (): JSX.Element => {
    if (availableModels.length === 0) {
      return <NoModelsInstalledEmptyState />;
    }

    return (
      <Stack spacing="lg">
        <ModelSection
          availableModels={availableModels}
          inputs={inputs}
          msaEnabledByModel={msaEnabledByModel}
          onAddModel={handleAddModel}
          onMSAEnabledChange={handleMSAEnabledChange}
          onRemoveModel={handleRemoveModel}
          selectedModels={selectedModels}
        />
        <InputsSection
          capabilities={capabilities}
          disabled={selectedModels.length === 0}
          inputs={inputs}
          onInputsChange={handleInputsChange}
        />
      </Stack>
    );
  };

  return (
    <div className={classes.root} {...getTestProps({component: 'NewPredictionPage', element: 'root'})}>
      <div className={classes.header}>
        <NewPredictionTopBar
          autoName={autoName}
          batchCount={selectedModels.length}
          disabledReason={disabledReason}
          isSubmitting={isSubmitting}
          batchName={batchName}
          onBack={onBack}
          onBatchNameChange={handleBatchNameChange}
          onSubmit={handleSubmit}
          predictionCount={predictionCount}
          resolvedBatchNames={resolvedBatchNames}
        />
      </div>
      <div className={classes.body}>
        <div className={classes.bodyInner}>
          <Stack spacing="lg">
            {creditError && (
              <Alert
                icon={<BenchlingIcon icon="exclamation-triangle" />}
                onClose={() => {
                  setCreditError(null);
                }}
                title={creditError.title}
                variant="attention"
                withCloseButton={true}
              >
                {creditError.detail}
              </Alert>
            )}
            {error && (
              <Alert
                icon={<BenchlingIcon icon="exclamation-circle" />}
                onClose={() => {
                  setError(null);
                }}
                title="Batch couldn't be created"
                variant="error"
                withCloseButton={true}
              >
                {error}
              </Alert>
            )}
            {renderContent()}
          </Stack>
        </div>
      </div>
    </div>
  );
}

function getKnownSubmitErrorMessage(submitError: unknown): string | null {
  const userMessage = net.getUserMessage(submitError) ?? getStructuredUserMessage(submitError);
  if (userMessage != null) {
    return userMessage;
  }
  return null;
}

function getErrorMessage(submitError: unknown): string | null {
  return submitError instanceof Error ? submitError.message : null;
}

function getStructuredUserMessage(submitError: unknown): string | null {
  if (typeof submitError !== 'object' || submitError == null) {
    return null;
  }
  const responseJSON = (submitError as {responseJSON?: unknown}).responseJSON;
  if (typeof responseJSON === 'object' && responseJSON != null) {
    const userMessage = (responseJSON as {userMessage?: unknown}).userMessage;
    return typeof userMessage === 'string' ? userMessage : null;
  }
  const userMessage = (submitError as {userMessage?: unknown}).userMessage;
  return typeof userMessage === 'string' ? userMessage : null;
}

const useStyles = createStyles((theme) => ({
  root: {
    backgroundColor: theme.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  header: {
    backgroundColor: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    flex: '0 0 auto',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
  },
  body: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  bodyInner: {
    margin: '0 auto',
    maxWidth: CONTENT_MAX_WIDTH_PX,
    padding: `${theme.spacing.xl} ${theme.spacing.xl} 64px`,
  },
}));
